import React, { useCallback, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { Button, Col, Row } from 'antd';

import useI18n from '../../common/hooks/useI18n';
import useSize from '../../common/hooks/useSize';

import { getHrefByCategory, useLinksGrid } from './linksGrid.provider';

type Props = {
  className?: string;
};

const Title = styled.h3`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
`;

const Divider = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  border-top: 1px solid #e0e0e0;
  background-color: #e0e0e0;
`;

const StyledRow = styled(Row)<{ $showAll: boolean }>`
  position: relative;
  max-height: ${(props) => (props.$showAll ? 'none' : '300px')};
  overflow: hidden;

  @media ${(props) => props.theme.breakpoints.xs} {
    margin-top: -16px;
  }
`;

const CategoryTitleButton = styled(Button)<{ $mode: string }>`
  padding: 0;
  ${(props) => props.theme.font.p1}

  span {
    font-weight: 600;
  }

  @media ${(props) => props.theme.breakpoints.mdu} {
    margin-bottom: 8px;
    ${(props) => (props.$mode === 'search' ? 'font-size: 90%;' : '')}
  }
`;

const LinksList = styled.ul<{ $mode: string }>`
  list-style: none;
  padding: 0;
  line-height: 1.2;

  li {
    margin-bottom: 8px;
  }

  @media ${(props) => props.theme.breakpoints.mdu} {
    ${(props) => (props.$mode === 'search' ? 'font-size: 90%;' : '')}
  }
`;

const GradientDiv = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 5vh;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 1)
  );
`;

const ShowMoreLessButton = styled(Button)`
  margin-top: 16px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const LinksGrid: React.FC<Props> = ({ className }) => {
  const i18n = useI18n();
  const { mode, linksMap } = useLinksGrid();

  const [showAll, setShowAll] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useSize(containerRef);

  const title = useMemo(() => i18n.t('linksGrid.title'), [i18n]);

  const getLinkItem = useCallback(
    (name: string, url: string) => (
      <li key={name}>
        <Link href={url}>{name}</Link>
      </li>
    ),
    []
  );

  const shouldSplit = useCallback(
    (category: string) => {
      return linksMap.get(category).length >= 9;
    },
    [linksMap]
  );

  const getSplittedLinks = useCallback(
    (category: string) => {
      const links = linksMap.get(category);
      return [
        links.slice(0, Math.ceil(links.length / 3)),
        links.slice(
          Math.ceil(links.length / 3),
          Math.ceil((links.length / 3) * 2)
        ),
        links.slice(Math.ceil((links.length / 3) * 2)),
      ];
    },
    [linksMap]
  );

  const renderLinksList = useCallback(
    (links: { name: string; url: string }[]) => (
      <LinksList as="ul" $mode={mode}>
        {links.map((link) => getLinkItem(link.name, link.url))}
      </LinksList>
    ),
    [getLinkItem, mode]
  );

  return (
    <div className={className}>
      <Title
        key={title.substring(0, 10) + new Date().getTime()}
        as="h3"
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <Divider />

      <StyledRow
        $showAll={showAll}
        justify="start"
        gutter={[16, 32]}
        ref={containerRef}
      >
        <>
          {Array.from(linksMap.keys()).map((category) => (
            <Col
              key={category}
              xs={24}
              sm={shouldSplit(category) ? 24 : 12}
              md={shouldSplit(category) ? 24 : 8}
            >
              <Link href={getHrefByCategory(category)} passHref>
                <CategoryTitleButton type="text" $mode={mode}>
                  {i18n.t(`header.${category}`)}
                </CategoryTitleButton>
              </Link>

              {shouldSplit(category) ? (
                <Row gutter={16}>
                  {[0, 1, 2].map((index) => (
                    <Col key={index} span={8}>
                      {renderLinksList(getSplittedLinks(category)[index])}
                    </Col>
                  ))}
                </Row>
              ) : (
                renderLinksList(linksMap.get(category))
              )}
            </Col>
          ))}

          {!showAll && containerSize && containerSize.height >= 300 && (
            <GradientDiv />
          )}
        </>
      </StyledRow>

      {containerSize && containerSize.height >= 300 && (
        <ShowMoreLessButton
          type="primary"
          onClick={() => setShowAll((current) => !current)}
        >
          {i18n.t(`actions.${showAll ? 'showLess' : 'showMore'}`)}
        </ShowMoreLessButton>
      )}
    </div>
  );
};

export default styled(LinksGrid)`
  position: relative;
  ${(props) => props.theme.font.p1}
  width: 100%;
  margin-bottom: 2rem;
`;
